/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { isFormulaEmpty, JeVariationStatus, JeVariationType } from '@gh/builder-shared-data';
import {
  conditionalCellRenderer,
  createDefaultGridModel,
  GREEN_MARKER_COLOR,
  notEmptyCellRenderer,
  rendererColorCircle,
  rendererIconByName,
  ROW_DIRTY_CLASS,
} from '@gh/common-internal-shared';
import { NumberTypes, State, TextTypes } from '@gh/common-shared';
import {
  buildEditableNumberColumn,
  buildEditableTextColumn,
  buildNumberColumn,
  gridCollectionComparator,
  GridModel,
  GridOptionsExt,
  gridValueCollectionComparator,
  LayoutBasedGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { ColDef } from 'ag-grid-community';
import { get, isNil, negate } from 'lodash';
import {
  getJeComponentId,
  JeComponent,
  jeComponentFieldState,
  jeCuttingListValueComparator,
  jeFormulaValueComparator,
  jeVariationComparator,
  validateJeComponentField,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import { JeDetailsState, queryJeEditable, queryJeUomRefItem } from '../je-details.state';

const quoteVariationRenderer = rendererIconByName('error', '#ff9e21');
const jobVariationRenderer = rendererIconByName('warning-var', '#ff9e21');
const jobVariationApprovedRenderer = rendererIconByName('done', '#666666');

@Injectable()
export class JeDetailsComponentGridDefinition extends LayoutBasedGridDefinitionWithModel {
  constructor(private state: State<JeDetailsState>,
              private commands: JeCommandService) {
    super();
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel<JeComponent>({
      editable: this.state.select(queryJeEditable),
      getId: getJeComponentId,
      validate: validateJeComponentField,
      getCellDef: jeComponentFieldState,
      command: (entity, name, value) => this.commands.componentSetField(getJeComponentId(entity), name, value),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      rowSelection: 'single',
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: JeComponent }) => !isNil(data.dirtyType),
      },
    };
  }

  buildColumnDefs(): Hash<ColDef> {
    return {
      cost: buildNumberColumn(NumberTypes.SignedPrice),
      extension: buildNumberColumn(NumberTypes.SignedPrice),
      description: buildEditableTextColumn(TextTypes.Text, this.model.getCellAccessor('description')),
      quantity: buildEditableNumberColumn(NumberTypes.SignedQuantity, this.model.getCellAccessor('quantity')),
      variation: {
        field       : 'variationStatus',
        comparator  : gridCollectionComparator(jeVariationComparator),
        cellRenderer: ({data: {variationStatus, variationType}}) => {
          if (variationStatus === JeVariationStatus.Confirmed &&
            (variationType === JeVariationType.Job || variationType === JeVariationType.Quotation)) {
            return jobVariationApprovedRenderer;
          } else {
            return variationStatus === JeVariationStatus.Unconfirmed ? {
              [JeVariationType.Quotation]: quoteVariationRenderer,
              [JeVariationType.Job]      : jobVariationRenderer,
            }[variationType] : '';
          }},
      },
      uomCode: {
        valueGetter: ({ data }: { data: any }) => get(this.state.project(queryJeUomRefItem, data.uomId), 'code', ''),
      },
      isCut: {
        field: 'cuttingList',
        comparator: gridValueCollectionComparator(jeCuttingListValueComparator),
        cellRenderer: notEmptyCellRenderer(rendererColorCircle(GREEN_MARKER_COLOR)),
      },
      isFormula: {
        field: 'formula',
        comparator: gridValueCollectionComparator(jeFormulaValueComparator),
        cellRenderer: conditionalCellRenderer(negate(isFormulaEmpty), rendererColorCircle(GREEN_MARKER_COLOR)),
      },
    };
  }
}
